import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileSpreadsheet,
  FileText,
  Printer,
  X,
  Download,
  Edit2,
  Check,
  Loader2,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrintPreview = ({
  isOpen,
  onClose,
  products,
  columns,
  filterBy,
  filterValue,
  token
}) => {
  const [companyName, setCompanyName] = useState("EAC");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef(null);
  const inputRef = useRef(null);

  // Format current date as DD-MM-YYYY
  const currentDate = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  }, []);

  // Column labels mapping
  const columnLabels = {
    serial_no: "S.No",
    product_name: "Product Name",
    unit: "Unit",
    generic_name: "Generic Name",
    company: "Company",
    box_packaging: "Box Packaging",
    weight_per_strip: "Weight",
    category: "Category",
    price: "Price",
    branded_mrp: "Branded MRP",
    suggested_usd_price: "Suggested $ Price",
    second_name: "2nd Name",
    last_update: "Last Update",
    image_link: "Image Link",
    narration: "Narration"
  };

  // Group products based on filter type
  const groupedProducts = useMemo(() => {
    if (!products || products.length === 0) return [];

    // Determine grouping field
    let groupField = null;
    if (filterBy === "generic_name") groupField = "generic_name";
    else if (filterBy === "company") groupField = "company";
    else if (filterBy === "category") groupField = "category";
    else if (filterBy === "branded_mrp") groupField = "branded"; // Special handling

    if (!groupField) {
      // No grouping, return flat list
      return [{ groupName: null, items: products }];
    }

    // Group products
    const groups = {};
    const uncategorised = [];

    products.forEach((product) => {
      let key;
      if (groupField === "branded") {
        // All branded products go under one group
        key = "BRANDED PRODUCTS";
      } else {
        key = (product[groupField] || "").toString().trim().toUpperCase();
        if (!key) {
          uncategorised.push(product);
          return;
        }
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(product);
    });

    // Sort groups alphabetically
    const sortedGroups = Object.keys(groups)
      .sort()
      .map((key) => ({
        groupName: key,
        items: groups[key]
      }));

    // Add uncategorised at the end if any
    if (uncategorised.length > 0) {
      sortedGroups.push({
        groupName: "UNCATEGORISED",
        items: uncategorised
      });
    }

    return sortedGroups;
  }, [products, filterBy]);

  // Calculate total pages (approximate for display)
  const totalProducts = products?.length || 0;
  const itemsPerPage = 25; // Approximate items per printed page
  const totalPages = Math.ceil(totalProducts / itemsPerPage);

  // Focus input when editing
  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingName]);

  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }

    const styles = `
      <style>
        @page {
          size: landscape;
          margin: 0.5in;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 10px;
          color: #333;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #4338CA;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        .header-left {
          font-size: 18px;
          font-weight: bold;
          color: #4338CA;
        }
        .header-right {
          font-size: 12px;
          color: #666;
        }
        .group-header {
          background: linear-gradient(to right, #4338CA, #6366F1);
          color: white;
          padding: 8px 12px;
          font-size: 14px;
          font-weight: bold;
          margin: 20px 0 10px 0;
          border-radius: 4px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
        }
        th {
          background: #F1F5F9;
          border: 1px solid #CBD5E1;
          padding: 6px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 9px;
          text-transform: uppercase;
        }
        td {
          border: 1px solid #E2E8F0;
          padding: 5px 8px;
          font-size: 9px;
        }
        tr:nth-child(even) {
          background: #F8FAFC;
        }
        .footer {
          position: fixed;
          bottom: 0.3in;
          right: 0.5in;
          font-size: 10px;
          color: #666;
        }
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${companyName} - Product Price List</title>
        ${styles}
      </head>
      <body>
        ${printContent.innerHTML}
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Handle Excel download — styled to match the on-screen format
  // (indigo title, gradient group headers, slate column headers, banded rows)
  const handleDownloadExcel = () => {
    if (!products || products.length === 0) {
      toast.error("No data to export");
      return;
    }

    setIsExporting(true);

    try {
      const wb = XLSX.utils.book_new();
      const wsData = [];

      // Styles (xlsx-js-style format)
      const titleStyle = {
        font: { name: "Arial", sz: 16, bold: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "4338CA" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "4338CA" } },
          bottom: { style: "thin", color: { rgb: "4338CA" } },
          left: { style: "thin", color: { rgb: "4338CA" } },
          right: { style: "thin", color: { rgb: "4338CA" } },
        },
      };
      const titleDateStyle = {
        font: { name: "Arial", sz: 11, italic: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "4338CA" } },
        alignment: { horizontal: "right", vertical: "center" },
      };
      const groupHeaderStyle = {
        font: { name: "Arial", sz: 13, bold: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "6366F1" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "4338CA" } },
          bottom: { style: "thin", color: { rgb: "4338CA" } },
        },
      };
      const columnHeaderStyle = {
        font: { name: "Arial", sz: 10, bold: true, color: { rgb: "1E293B" } },
        fill: { patternType: "solid", fgColor: { rgb: "F1F5F9" } },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "CBD5E1" } },
          bottom: { style: "thin", color: { rgb: "CBD5E1" } },
          left: { style: "thin", color: { rgb: "CBD5E1" } },
          right: { style: "thin", color: { rgb: "CBD5E1" } },
        },
      };
      const cellBase = {
        font: { name: "Arial", sz: 10, color: { rgb: "1E293B" } },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "E2E8F0" } },
          bottom: { style: "thin", color: { rgb: "E2E8F0" } },
          left: { style: "thin", color: { rgb: "E2E8F0" } },
          right: { style: "thin", color: { rgb: "E2E8F0" } },
        },
      };
      const cellAlt = {
        ...cellBase,
        fill: { patternType: "solid", fgColor: { rgb: "F8FAFC" } },
      };
      const priceCell = (base) => ({
        ...base,
        font: { ...base.font, bold: true, color: { rgb: "B45309" } },
        alignment: { horizontal: "right", vertical: "center" },
      });

      const styleMap = {}; // row,col -> style

      // Row 1: Title row spanning the full width
      const totalCols = 1 + columns.length;
      wsData.push([`${companyName} — Product Price List`, ...Array(totalCols - 2).fill(""), `Generated: ${currentDate}`]);
      styleMap[`${0},${0}`] = titleStyle;
      styleMap[`${0},${totalCols - 1}`] = titleDateStyle;
      const merges = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 2 } },
      ];

      wsData.push([]); // spacer

      let serialNo = 1;
      let firstHeaderWritten = false;

      groupedProducts.forEach((group) => {
        if (group.groupName) {
          // Group header row — merged across the full width
          const rowIdx = wsData.length;
          wsData.push([group.groupName, ...Array(totalCols - 1).fill("")]);
          styleMap[`${rowIdx},${0}`] = groupHeaderStyle;
          merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: totalCols - 1 } });

          // Column headers row
          const headerRowIdx = wsData.length;
          wsData.push(["S.No", ...columns.map((col) => columnLabels[col] || col)]);
          for (let c = 0; c < totalCols; c++) {
            styleMap[`${headerRowIdx},${c}`] = columnHeaderStyle;
          }
          firstHeaderWritten = true;
        } else if (!firstHeaderWritten) {
          // No grouping — write column headers once
          const headerRowIdx = wsData.length;
          wsData.push(["S.No", ...columns.map((col) => columnLabels[col] || col)]);
          for (let c = 0; c < totalCols; c++) {
            styleMap[`${headerRowIdx},${c}`] = columnHeaderStyle;
          }
          firstHeaderWritten = true;
        }

        group.items.forEach((product, idx) => {
          const rowIdx = wsData.length;
          const row = [serialNo++, ...columns.map((col) => product[col] || "")];
          wsData.push(row);
          const base = idx % 2 === 0 ? cellBase : cellAlt;
          for (let c = 0; c < totalCols; c++) {
            const colKey = c === 0 ? "serial_no" : columns[c - 1];
            const isPrice = colKey === "price" || colKey === "branded_mrp" || colKey === "suggested_usd_price";
            styleMap[`${rowIdx},${c}`] = isPrice ? priceCell(base) : base;
          }
        });

        wsData.push([]); // spacer after group
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Apply collected styles
      Object.entries(styleMap).forEach(([key, style]) => {
        const [r, c] = key.split(",").map(Number);
        const ref = XLSX.utils.encode_cell({ r, c });
        if (ws[ref]) {
          ws[ref].s = style;
        } else {
          ws[ref] = { v: "", t: "s", s: style };
        }
      });

      // Set merges + column widths + frozen header
      ws["!merges"] = merges;
      ws["!cols"] = [{ wch: 6 }, ...columns.map((col) => {
        if (col === "product_name" || col === "generic_name") return { wch: 32 };
        if (col === "company" || col === "category") return { wch: 18 };
        if (col === "image_link" || col === "narration") return { wch: 30 };
        return { wch: 14 };
      })];
      // Make rows that are group headers / titles taller
      ws["!rows"] = wsData.map((row, idx) => {
        if (idx === 0) return { hpt: 28 };
        const style = styleMap[`${idx},${0}`];
        if (style === groupHeaderStyle) return { hpt: 22 };
        if (style === columnHeaderStyle) return { hpt: 20 };
        return { hpt: 18 };
      });
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };

      XLSX.utils.book_append_sheet(wb, ws, "Price List");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

      saveAs(blob, `${companyName.replace(/\s+/g, "_")}_Price_List_${currentDate.replace(/-/g, "")}.xlsx`);
      toast.success(`Excel exported (${products.length} products, styled)`);
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file: " + (error.message || ""));
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Google Sheets export
  const handleExportGoogleSheets = () => {
    if (!products || products.length === 0) {
      toast.error("No data to export");
      return;
    }

    setIsExporting(true);

    try {
      // Create CSV data for Google Sheets import
      const csvData = [];
      
      // Add header info
      csvData.push([`${companyName} - Product Price List`, "", "", `Generated: ${currentDate}`]);
      csvData.push([]);
      
      let serialNo = 1;
      
      groupedProducts.forEach((group) => {
        if (group.groupName) {
          csvData.push([group.groupName]);
          csvData.push(["S.No", ...columns.map(col => columnLabels[col] || col)]);
        } else if (csvData.length === 2) {
          csvData.push(["S.No", ...columns.map(col => columnLabels[col] || col)]);
        }
        
        group.items.forEach((product) => {
          const row = [serialNo++, ...columns.map(col => {
            const val = product[col] || "";
            // Escape commas and quotes for CSV
            if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
              return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          })];
          csvData.push(row);
        });
        
        csvData.push([]);
      });

      // Convert to CSV string
      const csvString = csvData.map(row => row.join(",")).join("\n");
      
      // Create blob and download
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `${companyName.replace(/\s+/g, "_")}_Price_List_${currentDate.replace(/-/g, "")}.csv`);
      
      // Open Google Sheets import page
      const googleSheetsUrl = "https://docs.google.com/spreadsheets/create";
      window.open(googleSheetsUrl, "_blank");
      
      toast.success("CSV downloaded! Import it into the opened Google Sheets");
    } catch (error) {
      console.error("Google Sheets export error:", error);
      toast.error("Failed to export for Google Sheets");
    } finally {
      setIsExporting(false);
    }
  };

  // Handle PDF download — A4 portrait, header on every page, page numbers
  // (uses jsPDF + autotable; styled to match the on-screen color scheme)
  // Build the jsPDF doc + filename (shared by Download PDF and WhatsApp share)
  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 30;

    const headerColumns = ["S.No", ...columns.map((col) => columnLabels[col] || col)];

    const tableBody = [];
    let serialNo = 1;
    groupedProducts.forEach((group) => {
      if (group.groupName) {
        tableBody.push([{
          content: group.groupName,
          colSpan: headerColumns.length,
          styles: {
            fillColor: [99, 102, 241],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 11,
            halign: "left",
            cellPadding: 6,
          },
        }]);
      }
      group.items.forEach((product) => {
        tableBody.push([
          String(serialNo++),
          ...columns.map((col) => {
            const v = product[col];
            return v === null || v === undefined ? "" : String(v);
          }),
        ]);
      });
    });

    const drawHeaderFooter = () => {
      doc.setFillColor(67, 56, 202);
      doc.rect(0, 0, pageWidth, 50, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(`${companyName} — Product Price List`, margin, 32);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const meta = `Generated: ${currentDate}   ·   ${products.length} products`;
      const filterValueStr = (() => {
        const vals = Array.isArray(filterValue) ? filterValue : (filterValue ? [filterValue] : []);
        if (!vals.length) return "";
        return vals.length > 3 ? `${vals.slice(0, 3).join(", ")} +${vals.length - 3} more` : vals.join(", ");
      })();
      const filterMeta = filterBy ? `   ·   Filter: ${columnLabels[filterBy] || filterBy}${filterValueStr ? `: ${filterValueStr}` : ""}` : "";
      doc.text(meta + filterMeta, pageWidth - margin, 32, { align: "right" });

      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`Page ${pageNum} of ${pageCount}`, pageWidth / 2, pageHeight - 15, { align: "center" });
      doc.text(`${companyName}`, margin, pageHeight - 15);
      doc.text(currentDate, pageWidth - margin, pageHeight - 15, { align: "right" });
    };

    const priceCols = new Set();
    columns.forEach((col, idx) => {
      if (col === "price" || col === "branded_mrp" || col === "suggested_usd_price") {
        priceCols.add(idx + 1);
      }
    });

    autoTable(doc, {
      head: [headerColumns],
      body: tableBody,
      startY: 65,
      margin: { top: 65, bottom: 30, left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3, lineColor: [226, 232, 240], lineWidth: 0.5, overflow: "linebreak" },
      headStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: "bold", lineColor: [203, 213, 225], fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        if (data.section === "body" && priceCols.has(data.column.index)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [180, 83, 9];
          data.cell.styles.halign = "right";
        }
      },
      didDrawPage: drawHeaderFooter,
    });

    const filename = `${companyName.replace(/\s+/g, "_")}_Price_List_${currentDate.replace(/-/g, "")}.pdf`;
    return { doc, filename };
  };

  // Handle PDF download — A4 portrait, header on every page, page numbers
  // (uses jsPDF + autotable; styled to match the on-screen color scheme)
  const handleDownloadPDF = () => {
    if (!products || products.length === 0) {
      toast.error("No data to export");
      return;
    }
    setIsExporting(true);
    try {
      const { doc, filename } = buildPdfDoc();
      doc.save(filename);
      toast.success(`PDF exported (${products.length} products, A4 landscape)`);
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF: " + (error.message || ""));
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Share to WhatsApp — generates the PDF, opens the native share sheet
  // (which lists WhatsApp first on mobile). Falls back to download + wa.me link.
  const handleShareWhatsApp = async () => {
    if (!products || products.length === 0) {
      toast.error("No data to share");
      return;
    }
    setIsExporting(true);
    try {
      const { doc, filename } = buildPdfDoc();
      const blob = doc.output("blob");
      const pdfFile = new File([blob], filename, { type: "application/pdf" });

      const shareText = `${companyName} — Product Price List (${products.length} products, ${currentDate})`;

      // Web Share API with file attachment (iOS Safari, Android Chrome)
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [pdfFile] })
      ) {
        try {
          await navigator.share({
            files: [pdfFile],
            title: `${companyName} — Price List`,
            text: shareText,
          });
          toast.success("Shared");
          return;
        } catch (err) {
          // User cancelled the share sheet — silent no-op
          if (err && err.name === "AbortError") return;
          // Other errors fall through to wa.me fallback
          console.warn("navigator.share failed, falling back", err);
        }
      }

      // Fallback: download the PDF and open wa.me so the user can attach it
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
      toast.success("PDF downloaded — attach it in the WhatsApp window");
    } catch (error) {
      console.error("WhatsApp share error:", error);
      toast.error("Failed to share: " + (error.message || ""));
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Toolbar */}
        <div className="border-b bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-xl">
          {/* Row 1 (mobile + desktop): title + close on the right */}
          <div className="flex items-center justify-between px-3 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-0 gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Printer className="w-5 h-5 sm:w-6 sm:h-6 text-white flex-shrink-0" />
              <span className="text-base sm:text-lg font-semibold text-white truncate">Print Preview</span>
            </div>

            {/* Desktop actions inline */}
            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadExcel}
                disabled={isExporting}
                className="gap-2 bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-700"
                data-testid="download-excel-btn"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                Download Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportGoogleSheets}
                disabled={isExporting}
                className="gap-2 bg-blue-600 border-blue-500 text-white hover:bg-blue-700"
                data-testid="export-gsheets-btn"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Google Sheets
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                disabled={isExporting}
                data-testid="download-pdf-btn"
                className="gap-2 bg-rose-600 border-rose-500 text-white hover:bg-rose-700"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Download PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShareWhatsApp}
                disabled={isExporting}
                data-testid="share-whatsapp-btn"
                className="gap-2 bg-[#25D366] border-[#1ebe5d] text-white hover:bg-[#1ebe5d]"
                title="Share PDF on WhatsApp"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="gap-2 bg-orange-600 border-orange-500 text-white hover:bg-orange-700"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20"
                data-testid="print-preview-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Mobile-only close button on top row */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="sm:hidden text-white hover:bg-white/20 -mr-2 flex-shrink-0"
              data-testid="print-preview-close-btn-mobile"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Row 2 (mobile only): horizontally scrollable action chips */}
          <div className="sm:hidden px-3 pb-3 -mt-1 overflow-x-auto flex items-center gap-2 [scrollbar-width:none] [-ms-overflow-style:none] [&amp;::-webkit-scrollbar]:hidden">
            <Button
              size="sm"
              onClick={handleDownloadExcel}
              disabled={isExporting}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0 h-9"
              data-testid="download-excel-btn-mobile"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Excel
            </Button>
            <Button
              size="sm"
              onClick={handleExportGoogleSheets}
              disabled={isExporting}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0 h-9"
              data-testid="export-gsheets-btn-mobile"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Sheets
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={isExporting}
              className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white flex-shrink-0 h-9"
              data-testid="download-pdf-btn-mobile"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              PDF
            </Button>
            <Button
              size="sm"
              onClick={handleShareWhatsApp}
              disabled={isExporting}
              className="gap-1.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white flex-shrink-0 h-9"
              data-testid="share-whatsapp-btn-mobile"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              WhatsApp
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white flex-shrink-0 h-9"
              data-testid="print-btn-mobile"
            >
              <Printer className="w-4 h-4" />
              Print
            </Button>
          </div>
        </div>

        {/* Print Content */}
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div ref={printRef} className="bg-white rounded-lg shadow-lg p-8 min-h-[600px]">
            {/* Header with editable company name */}
            <div className="flex justify-between items-start border-b-2 border-indigo-600 pb-4 mb-6">
              <div className="flex items-center gap-2">
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      ref={inputRef}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      onBlur={() => setIsEditingName(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setIsEditingName(false);
                        if (e.key === "Escape") {
                          setCompanyName("EAC");
                          setIsEditingName(false);
                        }
                      }}
                      className="text-2xl font-bold text-indigo-700 border-indigo-300 w-64"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditingName(false)}
                      className="text-green-600"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-indigo-700">
                      {companyName} – Product Price List
                    </h1>
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="text-gray-400 hover:text-indigo-600 transition-colors no-print"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Generated Date:</p>
                <p className="text-lg font-semibold text-gray-800">{currentDate}</p>
              </div>
            </div>

            {/* Filter info */}
            {filterBy && (
              <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="text-sm text-indigo-700">
                  <strong>Filter:</strong>{" "}
                  {filterBy === "branded_mrp" ? "Branded Products Only" :
                   (() => {
                     const vals = Array.isArray(filterValue) ? filterValue : (filterValue ? [filterValue] : []);
                     return `${columnLabels[filterBy] || filterBy}${vals.length ? `: ${vals.join(", ")}` : ""}`;
                   })()}
                </span>
              </div>
            )}

            {/* Grouped content */}
            {groupedProducts.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-6">
                {/* Group header */}
                {group.groupName && (
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-t-lg font-semibold text-sm mb-0">
                    {group.groupName}
                    <span className="ml-2 text-indigo-200">({group.items.length} items)</span>
                  </div>
                )}

                {/* Table */}
                <table className={`w-full border-collapse text-sm ${group.groupName ? "rounded-b-lg" : "rounded-lg"} overflow-hidden`}>
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-300 px-3 py-2 text-left font-semibold text-xs uppercase text-slate-600 w-12">
                        S.No
                      </th>
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="border border-slate-300 px-3 py-2 text-left font-semibold text-xs uppercase text-slate-600"
                        >
                          {columnLabels[col] || col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((product, idx) => {
                      // Calculate global serial number
                      let globalIndex = idx + 1;
                      for (let i = 0; i < groupIndex; i++) {
                        globalIndex += groupedProducts[i].items.length;
                      }
                      
                      return (
                        <tr
                          key={product.id || idx}
                          className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                        >
                          <td className="border border-slate-200 px-3 py-2 text-center text-slate-500 font-mono">
                            {globalIndex}
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col}
                              className="border border-slate-200 px-3 py-2 text-slate-700"
                            >
                              {product[col] || "-"}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Footer with page numbers */}
            <div className="mt-8 pt-4 border-t border-gray-200 flex justify-between items-center text-sm text-gray-500">
              <span>Total: {totalProducts} products</span>
              <span>Page 1 of {totalPages || 1}</span>
            </div>
          </div>
        </div>

        {/* Bottom status bar */}
        <div className="px-6 py-3 border-t bg-slate-50 rounded-b-xl flex items-center justify-between text-sm text-slate-600">
          <span>
            Showing {totalProducts} products
            {filterBy && filterBy !== "branded_mrp" && filterValue && ` filtered by ${filterValue}`}
            {filterBy === "branded_mrp" && " (Branded Only)"}
          </span>
          <span className="text-xs text-slate-400">
            Tip: Click the company name to edit it
          </span>
        </div>
      </div>
    </div>
  );
};

export default PrintPreview;
